import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

const TOKEN_KEY = 'kche_admin_token';

export const tokenStore = {
    get: () => localStorage.getItem(TOKEN_KEY),
    set: (t) => localStorage.setItem(TOKEN_KEY, t),
    clear: () => localStorage.removeItem(TOKEN_KEY),
};

api.interceptors.request.use((config) => {
    const token = tokenStore.get();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error?.response?.status === 401) {
            tokenStore.clear();
            if (window.location.pathname.startsWith('/admin') && !window.location.pathname.endsWith('/login')) {
                window.location.href = '/admin/login';
            }
        }
        return Promise.reject(error);
    }
);

export const publicApi = {
    submitLead: (data) => api.post('/leads', data),
    submitReservation: (data) => api.post('/reservations', data),
    track: (data) => api.post('/analytics/track', data).catch(() => null),
    getContent: () => api.get('/public/content').then((r) => r.data),
    getSettings: () => api.get('/public/settings').then((r) => r.data),
    getAttractions: (category) => api.get('/public/attractions', { params: { category } }).then((r) => r.data),
    getEvents: () => api.get('/public/events').then((r) => r.data),
    getSocial: () => api.get('/public/social').then((r) => r.data),
    getMedia: (category) => api.get('/public/media', { params: { category } }).then((r) => r.data),
};

export const adminApi = {
    login: (email, password) => api.post('/auth/login', { email, password }).then((r) => r.data),
    me: () => api.get('/auth/me').then((r) => r.data),
    logout: () => api.post('/auth/logout').then((r) => r.data),

    dashboardStats: () => api.get('/admin/dashboard/stats').then((r) => r.data),
    dashboardTimeseries: (days = 7) => api.get('/admin/dashboard/timeseries', { params: { days } }).then((r) => r.data),
    recentLeads: (limit = 5) => api.get('/admin/dashboard/recent-leads', { params: { limit } }).then((r) => r.data),

    leads: {
        list: (params = {}) => api.get('/admin/leads', { params }).then((r) => r.data),
        get: (id) => api.get(`/admin/leads/${id}`).then((r) => r.data),
        update: (id, data) => api.patch(`/admin/leads/${id}`, data).then((r) => r.data),
        addNote: (id, text) => api.post(`/admin/leads/${id}/notes`, { text }).then((r) => r.data),
        delete: (id) => api.delete(`/admin/leads/${id}`).then((r) => r.data),
        exportCsv: () => api.get('/admin/leads/export/csv', { responseType: 'blob' }).then((r) => r.data),
    },
    reservations: {
        list: (params = {}) => api.get('/admin/reservations', { params }).then((r) => r.data),
        get: (id) => api.get(`/admin/reservations/${id}`).then((r) => r.data),
        update: (id, data) => api.patch(`/admin/reservations/${id}`, data).then((r) => r.data),
        addNote: (id, text) => api.post(`/admin/reservations/${id}/notes`, { text }).then((r) => r.data),
        delete: (id) => api.delete(`/admin/reservations/${id}`).then((r) => r.data),
    },
    attractions: {
        list: () => api.get('/admin/attractions').then((r) => r.data),
        create: (data) => api.post('/admin/attractions', data).then((r) => r.data),
        update: (id, data) => api.patch(`/admin/attractions/${id}`, data).then((r) => r.data),
        delete: (id) => api.delete(`/admin/attractions/${id}`).then((r) => r.data),
    },
    events: {
        list: () => api.get('/admin/events').then((r) => r.data),
        create: (data) => api.post('/admin/events', data).then((r) => r.data),
        update: (id, data) => api.patch(`/admin/events/${id}`, data).then((r) => r.data),
        delete: (id) => api.delete(`/admin/events/${id}`).then((r) => r.data),
    },
    content: {
        list: () => api.get('/admin/content').then((r) => r.data),
        upsert: (key, value) => api.put('/admin/content', { key, value }).then((r) => r.data),
    },
    media: {
        list: () => api.get('/admin/media').then((r) => r.data),
        create: (data) => api.post('/admin/media', data).then((r) => r.data),
        update: (id, data) => api.patch(`/admin/media/${id}`, data).then((r) => r.data),
        delete: (id) => api.delete(`/admin/media/${id}`).then((r) => r.data),
    },
    social: {
        list: () => api.get('/admin/social').then((r) => r.data),
        create: (data) => api.post('/admin/social', data).then((r) => r.data),
        update: (id, data) => api.patch(`/admin/social/${id}`, data).then((r) => r.data),
        delete: (id) => api.delete(`/admin/social/${id}`).then((r) => r.data),
    },
    settings: {
        list: () => api.get('/admin/settings').then((r) => r.data),
        upsert: (key, value) => api.put('/admin/settings', { key, value }).then((r) => r.data),
    },
    activity: (limit = 100) => api.get('/admin/activity', { params: { limit } }).then((r) => r.data),
    analyticsSummary: (days = 30) => api.get('/admin/analytics/summary', { params: { days } }).then((r) => r.data),
    backup: {
        downloadComplete: () => api.post('/admin/backup/complete', null, { responseType: 'blob' }),
    },
};
